import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React from 'react';

import { CsvIssue } from '../types';

const MAX_DISPLAYED_ISSUES = 200;

interface IssuesTableProps {
  issues: CsvIssue[];
}

export const IssuesTable: React.FC<IssuesTableProps> = ({ issues }) => {
  const sorted = [...issues].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'error' ? -1 : 1;
    return (a.row ?? 0) - (b.row ?? 0);
  });
  const displayed = sorted.slice(0, MAX_DISPLAYED_ISSUES);
  const errorCount = issues.filter(
    (issue) => issue.severity === 'error'
  ).length;
  const warningCount = issues.length - errorCount;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">File issues</h3>
        {errorCount > 0 && (
          <Chip color="danger" variant="flat" size="sm">
            {errorCount} error{errorCount === 1 ? '' : 's'}
          </Chip>
        )}
        {warningCount > 0 && (
          <Chip color="warning" variant="flat" size="sm">
            {warningCount} warning{warningCount === 1 ? '' : 's'}
          </Chip>
        )}
      </div>
      <Table aria-label="CSV validation issues" removeWrapper isStriped>
        <TableHeader>
          <TableColumn className="w-20">LINE</TableColumn>
          <TableColumn className="w-36">COLUMN</TableColumn>
          <TableColumn className="w-28">SEVERITY</TableColumn>
          <TableColumn>MESSAGE</TableColumn>
        </TableHeader>
        <TableBody>
          {displayed.map((issue, index) => (
            <TableRow key={index}>
              <TableCell>{issue.row ?? '—'}</TableCell>
              <TableCell>
                {issue.column ? (
                  <span className="font-mono text-sm">{issue.column}</span>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>
                <Chip
                  color={issue.severity === 'error' ? 'danger' : 'warning'}
                  variant="flat"
                  size="sm"
                >
                  {issue.severity}
                </Chip>
              </TableCell>
              <TableCell>{issue.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {sorted.length > MAX_DISPLAYED_ISSUES && (
        <p className="text-sm text-default-500">
          Showing the first {MAX_DISPLAYED_ISSUES} of {sorted.length} issues.
        </p>
      )}
    </div>
  );
};
